import React from 'react';
import { withTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';

import Media from '@core/dashboard/components/AppLoader/Media';
import { useMytaverseEvent } from '@core/dashboard/providers';

import { IAppLoader } from '../interfaces';
import { ReactComponent as WarningIcon } from '@public/images/loading_warning.svg';

import { useStyles } from './styles';

import AppWrapper from '@components/AppWrapper';
import PageWrapper from '@components/PageWrapper';
import CircularProgress from '@components/CircularProgress';
import ContentWrapper from '@components/ContentWrapper';
import Button from '@mui/material/Button';
import { useModalStyles } from '@core/myta/hooks/modalStyles';
import LoaderText from '@core/dashboard/components/AppLoader/v2/LoaderText';

/**
 * Renders the pre-join loading screen: a countdown while the streaming
 * session spins up, then a "Teleport to Experience" button once it's ready.
 *
 * @param props.progressValue - Load percentage (0-100); drives which loading
 *   phase's countdown `LoaderText` shows.
 * @param props.showTeleportButton - Whether the session is ready, swapping
 *   the countdown for the teleport button.
 * @param props.handleTeleportButtonClick - Called when the teleport button
 *   is clicked.
 */
const AppLoader = ({
  progressValue,
  t: translate,
  showTeleportButton,
  handleTeleportButtonClick,
}: IAppLoader) => {
  const { currentEvent } = useMytaverseEvent();

  const classes = useStyles();
  const modalClasses = useModalStyles();

  const primaryButtonSx = {
    ...modalClasses.button,
    ...modalClasses.buttonPrimary,
  };

  const currentStep = (value: number) => {
    if (value <= 75) {
      return 1;
    }

    if (value <= 90) {
      return 2;
    }

    return 3;
  };

  return (
    <AppWrapper
      isAuthorized
      backdrop={
        currentEvent && (
          <Box sx={classes.media}>
            <Media loadingAsset={currentEvent?.loadingScreenAsset} />
          </Box>
        )
      }
    >
      <PageWrapper
        title={translate('loading.launchingExperience')}
        bottomElement={
          showTeleportButton && handleTeleportButtonClick ? (
            <Button
              sx={primaryButtonSx}
              variant="contained"
              size="large"
              onClick={() => handleTeleportButtonClick()}
              data-testid="teleport-to-experience-button"
            >
              {translate('playScreen.play')}
            </Button>
          ) : progressValue ? (
            <Box sx={classes.progressWrap}>
              <Box sx={classes.progressWrapCircle}>
                <CircularProgress
                  value={progressValue}
                  dataTestId={'loading-event-progress'}
                />
              </Box>
              <LoaderText step={currentStep(progressValue)} />
            </Box>
          ) : (
            ''
          )
        }
      >
        <ContentWrapper
          subtitle={translate('loading.text1')}
          text={translate('loading.text2')}
        >
          <Box sx={classes.warning}>
            <WarningIcon />
            <Box>
              <Typography sx={classes.warningText}>
                {translate('loading.text3')}
              </Typography>
              <Typography sx={classes.warningText}>
                {translate('loading.text4')}
              </Typography>
            </Box>
          </Box>
        </ContentWrapper>
      </PageWrapper>
    </AppWrapper>
  );
};

export default withTranslation()(AppLoader);
